import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { motion, animate, useMotionValue } from "framer-motion";
import { useViewModeStore } from "../../store/viewMode.store";
import { User, Wrench } from "lucide-react";

// RTL languages that need flip
const RTL_LANGUAGES = ['ar', 'darija_ar'];

const SPRING = {
    type: "spring" as const,
    stiffness: 600,
    damping: 40,
    mass: 0.8,
};

type Mode = "client" | "provider";

interface ModeToggleProps {
    isProvider: boolean;
}

export function ModeToggle({ isProvider }: ModeToggleProps) {
    const { t, i18n } = useTranslation();
    const mode = useViewModeStore((s) => s.mode);
    const toggleMode = useViewModeStore((s) => s.toggleMode);

    // Detect RTL mode
    const isRTL = RTL_LANGUAGES.includes(i18n.language);

    const trackRef = useRef<HTMLDivElement | null>(null);
    const roRef = useRef<ResizeObserver | null>(null);
    const x = useMotionValue(0);

    const [maxX, setMaxX] = useState(0);
    const [knobW, setKnobW] = useState(0);

    const draggingRef = useRef(false);
    const [dragging, setDragging] = useState(false);
    const hasSnappedRef = useRef(false);

    const pad = 4;

    // Callback ref: measures the track as soon as it mounts (even if isProvider starts false)
    const trackCallbackRef = useCallback((el: HTMLDivElement | null) => {
        // Disconnect previous observer
        if (roRef.current) {
            roRef.current.disconnect();
            roRef.current = null;
        }

        trackRef.current = el;
        if (!el) {
            hasSnappedRef.current = false;
            return;
        }

        const compute = () => {
            const trackWidth = el.clientWidth;
            const inner = Math.max(0, trackWidth - pad * 2);
            const half = inner / 2;
            setKnobW(half);
            setMaxX(half);
        };

        compute();

        roRef.current = new ResizeObserver(() => compute());
        roRef.current.observe(el);
    }, []);

    // In RTL, the visual is flipped: client on right, provider on left
    // So in RTL: provider mode = x at 0, client mode = x at maxX
    const targetX = useMemo(() => {
        if (isRTL) {
            return mode === "provider" ? 0 : maxX;
        }
        return mode === "provider" ? maxX : 0;
    }, [mode, maxX, isRTL]);

    // Snap to correct position on first measurement, before paint
    useLayoutEffect(() => {
        if (maxX === 0 || hasSnappedRef.current) return;
        hasSnappedRef.current = true;
        x.set(targetX);
    }, [maxX, targetX, x]);

    // Animate on subsequent mode changes
    useEffect(() => {
        if (!hasSnappedRef.current) return;
        const controls = animate(x, targetX, SPRING);
        return () => controls.stop();
    }, [mode, targetX, x]);

    const setMode = (next: Mode) => {
        if (next !== mode) toggleMode();
    };

    const onClickSide = (next: Mode) => {
        if (draggingRef.current) return;
        setMode(next);
    };

    const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleMode();
        }
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            setMode("client");
        }
        if (e.key === "ArrowRight") {
            e.preventDefault();
            setMode("provider");
        }
    };

    if (!isProvider) return null;

    return (
        <div className="select-none">
            <div
                ref={trackCallbackRef}
                className="relative h-8 w-[4.5rem] xl:w-44 rounded-full p-1 bg-gray-200 ring-1 ring-gray-300 overflow-hidden"
                role="switch"
                aria-checked={mode === "provider"}
                tabIndex={0}
                onKeyDown={onKeyDown}
                onClickCapture={(e) => {
                    if (draggingRef.current) {
                        e.stopPropagation();
                        e.preventDefault();
                    }
                }}
            >
                {/* Zones cliquables */}
                <div className="relative z-10 grid h-full grid-cols-2">
                    <button
                        type="button"
                        className="h-full rounded-full flex items-center justify-center gap-1 text-[10px] font-medium text-gray-500 focus:outline-none"
                        onClick={() => onClickSide("client")}
                        aria-label={t('profile.viewAsClient')}
                    >
                        <User className="w-3 h-3 shrink-0" />
                        <span className="hidden xl:inline">{t('profile.client')}</span>
                    </button>

                    <button
                        type="button"
                        className="h-full rounded-full flex items-center justify-center gap-1 text-[10px] font-medium text-gray-500 focus:outline-none"
                        onClick={() => onClickSide("provider")}
                        aria-label={t('profile.becomeProvider')}
                    >
                        <Wrench className="w-3 h-3 shrink-0" />
                        <span className="hidden xl:inline">{t('profile.provider')}</span>
                    </button>
                </div>

                {/* Knob draggable — invisible until measured to prevent wrong-position flash */}
                <motion.div
                    className={`absolute top-1 left-1 z-20 h-6 rounded-full bg-white shadow-md ring-1 ring-gray-200 flex items-center justify-center cursor-grab active:cursor-grabbing overflow-hidden ${knobW > 0 ? "visible" : "invisible"}`}
                    style={{ width: knobW || "50%", x }}
                    drag="x"
                    dragConstraints={{ left: 0, right: maxX }}
                    dragElastic={0.08}
                    dragMomentum={false}
                    onDragStart={() => {
                        draggingRef.current = true;
                        setDragging(true);
                    }}
                    onDragEnd={() => {
                        const current = x.get();
                        const threshold = maxX / 2;

                        let next: Mode;
                        if (isRTL) {
                            next = current >= threshold ? "client" : "provider";
                        } else {
                            next = current >= threshold ? "provider" : "client";
                        }

                        if (next !== mode) toggleMode();

                        let snapX: number;
                        if (isRTL) {
                            snapX = next === "provider" ? 0 : maxX;
                        } else {
                            snapX = next === "provider" ? maxX : 0;
                        }
                        animate(x, snapX, SPRING);

                        window.setTimeout(() => {
                            draggingRef.current = false;
                            setDragging(false);
                        }, 0);
                    }}
                >
                    <span className="text-[10px] font-semibold text-blue-600 flex items-center gap-1 px-1.5 whitespace-nowrap">
                        {!dragging && (
                            mode === "client" ? (
                                <><User className="w-3 h-3 shrink-0" /><span className="hidden xl:inline">{t('profile.client')}</span></>
                            ) : (
                                <><Wrench className="w-3 h-3 shrink-0" /><span className="hidden xl:inline">{t('profile.provider')}</span></>
                            )
                        )}
                    </span>
                </motion.div>
            </div>
        </div>
    );
}
